import { forwardRef, type HTMLAttributes, type ReactNode } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

/**
 * Pill — status badge primitive replacing shadcn/ui Badge.
 *
 * Per Story 1.10 and DESIGN_SYSTEM.md §8.3:
 *   • Rounded 999px (`rounded-full`)
 *   • font-size 11px / weight 500
 *   • padding 3px 10px
 *   • 7 variants: default, success, warn, error, info, accent, live
 *   • Optional `dot` (6×6px). For `live`, dot pulses (animate-pulse).
 *
 * Token note: the project tokens are named `warning` / `warning-soft`
 * (see globals.css), but the public API uses the spec-friendly `warn`
 * variant name — mapping happens here invisibly to consumers.
 */
const pillVariants = cva(
  [
    'inline-flex items-center gap-1.5',
    'rounded-full border',
    'text-[11px] font-medium',
    'px-[10px] py-[3px]',
    'select-none align-middle',
  ].join(' '),
  {
    variants: {
      variant: {
        default: 'bg-panel-solid border-hair text-ink-2',
        // O TEXTO é `text-X-ink`, e não `text-X` (#496): o mesmo tom, mais escuro
        // só no claro, onde o tom sobre o `-soft` composto no `--sunk` media
        // 3,80–4,43:1. O fundo e a borda ficam no tom. O `default` e o `accent`
        // têm fundo opaco e passam (7,08 e 5,07). Travado em
        // `__tests__/contraste-do-claro.test.ts`.
        success: 'bg-success-soft border-success/30 text-success-ink',
        warn: 'bg-warning-soft border-warning/30 text-warning-ink',
        error: 'bg-error-soft border-error/30 text-error-ink',
        info: 'bg-info-soft border-info/30 text-info-ink',
        accent: 'bg-accent border-transparent text-accent-fg',
        // `live` = EM CURSO, e um processo em curso não é um êxito.
        //
        // Era idêntica à `accent`: verde de marca sólido, o tratamento mais alto
        // do sistema, aplicado a "Processando". Lia-se como "concluída com
        // êxito" — e ao lado de uma pill `success` (que era o MESMO verde) a
        // única diferença era o ponto a pulsar. Agora o azul diz "a acontecer" e
        // o pulsar diz "agora"; o verde fica reservado à marca, ao mérito e ao
        // êxito real.
        live: 'bg-info-soft border-info/30 text-info-ink',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)

export type PillVariant = NonNullable<VariantProps<typeof pillVariants>['variant']>

export interface PillProps
  extends HTMLAttributes<HTMLSpanElement>, VariantProps<typeof pillVariants> {
  /** Render a 6×6px dot before the children. Pulses when `variant="live"`. */
  dot?: boolean
  children: ReactNode
}

export const Pill = forwardRef<HTMLSpanElement, PillProps>(
  ({ className, variant, dot, children, ...props }, ref) => {
    // Live variant always shows a pulsing dot; explicit `dot` prop still works
    // for any variant so callers can opt in (AC8).
    const showDot = dot ?? variant === 'live'
    const pulse = variant === 'live'

    return (
      <span ref={ref} className={cn(pillVariants({ variant }), className)} {...props}>
        {showDot ? (
          <span
            aria-hidden="true"
            className={cn(
              'inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-current',
              pulse && 'animate-pulse',
            )}
          />
        ) : null}
        {children}
      </span>
    )
  },
)
Pill.displayName = 'Pill'

export { pillVariants }
